/**
 * EN MÅNED SOM RUTENETT — uker som rader, hendelsene på dagen sin (sak 229, spillestedets
 * «Arrangementer» i kalendervisning).
 *
 * Generisk med vilje: den kjenner bare en dato, et navn og en farge per hendelse. Hvilken måned som
 * vises og hva et klikk åpner, eier siden. Formen er den samme som konsertlistas månedsvisning
 * (`ConcertsListPage`), og Glass 2-reglene gjelder: datonavigasjonen er én kapsel (§16.5 punkt 6),
 * hendelsene tones i kategorifargen (punkt 11), ukedagene står i setningsform (punkt 7).
 */
import type { Locale } from '@tvitvi/shared';
import type { ReactNode } from 'react';

import { CategoryDot, categoryFill } from '@/components/ui/grid-cell';
import { PeriodNav } from '@/components/ui/PeriodNav';
import { cn } from '@/lib/cn';
import { intlTag } from '@/lib/format';

export interface MonthGridItem {
  id: string;
  /** `YYYY-MM-DD`, dagen hendelsen står på. */
  date: string;
  label: string;
  /** En token (`var(--color-cat-N)`), aldri en hex. */
  colour: string;
  /** Tegnes dempet og overstreket — et avlyst arrangement står, men leses som borte. */
  struck?: boolean;
  /** Foreløpig: et hold eller en forespørsel (QA15-S B3). Stiplet kant, svakere fyll. */
  tentative?: boolean;
}

export interface MonthGridProps {
  /** `YYYY-MM`. */
  month: string;
  items: readonly MonthGridItem[];
  locale: Locale;
  onMonth: (month: string) => void;
  onOpen: (id: string) => void;
  backLabel: string;
  forwardLabel: string;
  /** `<testId>`, `<testId>-nav`, `<testId>-<item.id>`, `<testId>-day-<date>`. */
  testId: string;
}

/** `2027-03` → første og siste dag i måneden, som datoer. */
export function monthBounds(month: string): { from: string; to: string } {
  const [year, monthNumber] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year ?? 1970, monthNumber ?? 1, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** `2027-03` flyttet `delta` måneder. */
export function shiftMonth(month: string, delta: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const value = new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1 + delta, 1));
  return value.toISOString().slice(0, 7);
}

function monthLabel(month: string, locale: Locale): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const text = new Intl.DateTimeFormat(intlTag(locale), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1, 1)));
  return text.charAt(0).toLocaleUpperCase(intlTag(locale)) + text.slice(1);
}

export function MonthGrid({
  month,
  items,
  locale,
  onMonth,
  onOpen,
  backLabel,
  forwardLabel,
  testId,
}: MonthGridProps): ReactNode {
  const [year, monthNumber] = month.split('-').map(Number);
  const first = new Date(Date.UTC(year ?? 1970, (monthNumber ?? 1) - 1, 1));
  const days = new Date(Date.UTC(year ?? 1970, monthNumber ?? 1, 0)).getUTCDate();
  /* Mandag først. */
  const lead = (first.getUTCDay() + 6) % 7;
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = new Map<string, MonthGridItem[]>();
  for (const item of items) {
    const bucket = byDay.get(item.date) ?? [];
    bucket.push(item);
    byDay.set(item.date, bucket);
  }
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(intlTag(locale), { weekday: 'short', timeZone: 'UTC' }).format(
      new Date(Date.UTC(2024, 0, 1 + index)),
    ),
  );

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="controls-on-backdrop flex items-center gap-2">
        <PeriodNav
          label={monthLabel(month, locale)}
          labelAs="h2"
          labelClassName="min-w-40 text-sm font-semibold"
          backLabel={backLabel}
          forwardLabel={forwardLabel}
          onBack={() => onMonth(shiftMonth(month, -1))}
          onForward={() => onMonth(shiftMonth(month, 1))}
          testId={`${testId}-nav`}
        />
      </div>
      <div className="glass-content grid grid-cols-7 overflow-hidden">
        {weekdays.map((day) => (
          <div
            key={day}
            className="border-b border-default px-2 py-1 text-xs font-medium text-muted"
          >
            {day}
          </div>
        ))}
        {cells.map((day, index) => (
          <div
            key={day ?? `pad-${String(index)}`}
            data-testid={day === null ? undefined : `${testId}-day-${day}`}
            className={cn(
              'min-h-24 border-b border-r border-subtle p-1.5 [&:nth-child(7n)]:border-r-0',
              day === null && 'bg-[var(--fill-tonal)]',
            )}
          >
            {day === null ? null : (
              <>
                <div className="numeric text-2xs text-muted">{Number(day.slice(8))}</div>
                <div className="mt-1 flex flex-col gap-1">
                  {(byDay.get(day) ?? []).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onOpen(item.id)}
                      className={cn(
                        'flex min-w-0 items-center gap-1.5 rounded-[var(--radius-xs)] px-1.5 py-0.5 text-left text-2xs ring-focus hover:brightness-95',
                        item.struck === true ? 'text-muted line-through' : 'text-default',
                        item.tentative === true && 'border border-dashed',
                      )}
                      style={
                        item.tentative === true
                          ? { borderColor: item.colour, backgroundColor: 'transparent' }
                          : categoryFill(item.colour)
                      }
                      data-tentative={item.tentative === true ? 'true' : undefined}
                      data-struck={item.struck === true ? 'true' : undefined}
                      data-testid={`${testId}-${item.id}`}
                    >
                      <CategoryDot colour={item.colour} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
